//angular imports
import { Injectable, OnDestroy, signal, WritableSignal } from "@angular/core";
import { Subject } from "rxjs";

//local imports
import {
  CatalogHttpService,
  GetCatalogResponse,
  CatalogDataChangedMessage,
  CatalogManagementModel,
  DepartmentStateHandler,
  RoomStateHandler,
  ProgramStateHandler,
  ProjectStateHandler,
  CategoryStateHandler,
  RoomHttpService,
  RoomPropertyStateHandler,
  DepartmentHttpService,
  ProgramHttpService,
  ProjectHttpService,
  RoomContentHttpService,
  CatalogFilterStateHandler,
  RoomNotFoundMessage,
  DepartmentNotFoundMessage,
  RoomModel,
  ProgramModel,
  ProjectModel,
  DepartmentModel,
  CategoryModel,
  RoomContentModel,
  RoomPropertyModel,
  DepartmentPropertyModel,
  ProgramPropertyModel,
  ProjectPropertyModel,
  RoomContentPropertyModel,
  CatalogRouteStateHandler,
  GetCarouselRequest,
  MenuModel,
  PrintRoomPropertiesLoadedMessage, 
} from "@app/catalog";

import { 
  ProgramNotFoundMessage,
  ProjectNotFoundMessage,
} from "@app/catalog-common";

import {
  CategoryNotFoundMessage,
  CategoryNotFoundFault,
  DepartmentNotFoundFault,
  ProgramNotFoundFault,
  ProjectNotFoundFault,
  RoomNotFoundFault,
} from "@app/catalog-common";

import {
  ToastLevel,
  ToastMessage
} from "@app/components";

import { Messenger } from "@app/shared";
import { CoreStateService } from "@app/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UnhandledErrorMessage } from "@app/app-common";

interface FilterOptions {
  roomId?: string;
  departmentId?: string;
  programId?: string;
  projectId?: string;
  roomContentId?: string;
  categoryId?: string;
  searchValues?: string[];
}

// Request queue item
class QueuedCarouselRequest {
  public roomId?: string;
  public departmentId?: string;
  public programId?: string;
  public projectId?: string;
  public roomContentId?: string;
  public categoryId?: string;
  public debounceMs?: number = 500;
  public searchValues?: string[];

  constructor(init: Partial<QueuedCarouselRequest>) {
    Object.assign(this, init);
  }

  // Check if two requests are equivalent
  public static equals(prev: QueuedCarouselRequest, current: QueuedCarouselRequest): boolean {
    return prev?.roomId === current?.roomId &&
      prev?.departmentId === current?.departmentId &&
      prev?.programId === current?.programId &&
      prev?.projectId === current?.projectId &&
      prev?.roomContentId === current?.roomContentId &&
      prev?.categoryId === current?.categoryId &&
      JSON.stringify(prev?.searchValues) === JSON.stringify(current?.searchValues);
  }
}

@Injectable({
  providedIn: "root",
})
export class CatalogStateService implements OnDestroy {
  private _initialized = false;
  private _requestQueue$ = new Subject<QueuedCarouselRequest>();


  private _roomStateHandler: RoomStateHandler;
  private _departmentHandler: DepartmentStateHandler;
  private _programStateHandler: ProgramStateHandler;
  private _projectStateHandler: ProjectStateHandler;
  private _categoryStateHandler: CategoryStateHandler;
  private _catalogFilterStateHandlder: CatalogFilterStateHandler;
  public _catalogRouteStateHandler: CatalogRouteStateHandler;
  private _roomPropertyStateHandler: RoomPropertyStateHandler;

  public RoomPropertiesState = new Map<string, RoomPropertyModel>();
  public DepartmentPropertiesState: WritableSignal<DepartmentPropertyModel[]> = signal<DepartmentPropertyModel[]>([]);
  public ProgramPropertiesState: WritableSignal<ProgramPropertyModel[]> = signal<ProgramPropertyModel[]>([]);
  public ProjectPropertiesState: WritableSignal<ProjectPropertyModel[]> = signal<ProjectPropertyModel[]>([]);
  public RoomContentPropertiesState: WritableSignal<RoomContentPropertyModel[]> = signal<RoomContentPropertyModel[]>([]);

  public CatalogManagementState: CatalogManagementModel;
  public CatalogMenuState: MenuModel;
  public isLoading = signal(false);

  constructor(
    private catalogHttpService: CatalogHttpService,
    private roomHttpService: RoomHttpService,
    private departmentHttpService: DepartmentHttpService,
    private programHttpService: ProgramHttpService,
    private projectHttpService: ProjectHttpService,
    private roomContentHttpService: RoomContentHttpService,
    private messenger: Messenger,
    private coreStateService: CoreStateService,
    private router: Router
  ) {
    this._roomStateHandler = new RoomStateHandler();
    this._departmentHandler = new DepartmentStateHandler();
    this._programStateHandler = new ProgramStateHandler();
    this._projectStateHandler = new ProjectStateHandler();
    this._categoryStateHandler = new CategoryStateHandler();
    this._roomPropertyStateHandler = new RoomPropertyStateHandler();
    this._catalogFilterStateHandlder = new CatalogFilterStateHandler();
    this._catalogRouteStateHandler = new CatalogRouteStateHandler();
  }

  public async initialize(route: ActivatedRoute) {
    if (this._initialized) return;

    this._roomStateHandler.initialize(this, this.messenger);
    this._departmentHandler.initialize(this, this.messenger);
    this._programStateHandler.initialize(this, this.messenger);
    this._projectStateHandler.initialize(this, this.messenger);
    this._categoryStateHandler.initialize(this, this.messenger);
    this._roomPropertyStateHandler.initialize(this, this.messenger);
    this._catalogFilterStateHandlder.initialize(this, this.messenger, this.router);
    this._catalogRouteStateHandler.initialize(this, this.messenger, route, this.router);

    this.CatalogManagementState = new CatalogManagementModel();
    this.CatalogMenuState = new MenuModel();

    this._initialized = true;
  }



  public async loadCarousels(options?: FilterOptions) {
    const request = new QueuedCarouselRequest({
      roomId: options?.roomId,
      departmentId: options?.departmentId,
      programId: options?.programId,
      projectId: options?.projectId,
      roomContentId: options?.roomContentId,
      categoryId: options?.categoryId,
      searchValues: options?.searchValues
    });


    const getCatalogRequest = this.buildRequest(request);
    let getCatalogHttpResult = this.catalogHttpService.getCarousels(getCatalogRequest);

    try {
      this.isLoading.set(true);
      
      let getCatalogResponse = await getCatalogHttpResult.response;

      this.mapResponseToState(this.coreStateService.testMode(), getCatalogResponse, request);

      this.messenger.send(new CatalogDataChangedMessage(this.CatalogManagementState));

      console.log('fetch from service complete');

    } catch (error) {
      this.handleError(error);
      
      // Still emit an empty state
      this.messenger.send(new CatalogDataChangedMessage(this.CatalogManagementState));
    } finally {
      this.isLoading.set(false);
    }
  }


  private buildRequest = (fetchRequest: QueuedCarouselRequest) => {
    const request = new GetCarouselRequest();
    request.roomId = fetchRequest.roomId;
    request.departmentId = fetchRequest.departmentId;
    request.programId = fetchRequest.programId;
    request.projectId = fetchRequest.projectId;
    request.roomContentId = fetchRequest.roomContentId;
    request.categoryId = fetchRequest.categoryId;
    request.search = fetchRequest.searchValues;
    return request;
  }

  private handleError(error: any) {
    if (error instanceof ProgramNotFoundFault) {
      let program = this.CatalogManagementState.programs().find(
        (item) => item.id === error.programId,
      );
      this.messenger.send(
        ToastMessage.create(
          new ProgramNotFoundMessage(program),
          ToastLevel.Warning,
        ),
      );
    } else if (error instanceof ProjectNotFoundFault) {
      let project = this.CatalogManagementState.projects().find(
        (item) => item.id === error.projectId,
      );
      this.messenger.send(
        ToastMessage.create(
          new ProjectNotFoundMessage(project),
          ToastLevel.Warning,
        ),
      );
    } else if (error instanceof RoomNotFoundFault) {
      let room = this.CatalogManagementState.rooms().find(
        (item) => item.id === error.roomId,
      );
      this.messenger.send(
        ToastMessage.create(
          new RoomNotFoundMessage(room),
          ToastLevel.Warning,
        ),
      );
    } else if (error instanceof DepartmentNotFoundFault) {
      let department = this.CatalogManagementState.departments().find(
        (item) => item.id === error.departmentId,
      );
      this.messenger.send(
        ToastMessage.create(
          new DepartmentNotFoundMessage(department),
          ToastLevel.Warning,
        ),
      );
    } else if (error instanceof CategoryNotFoundFault) {
      let category = this.CatalogManagementState.categories().find(
        (item) => item.id === error.categoryId,
      );
      this.messenger.send(
        ToastMessage.create(
          new CategoryNotFoundMessage(category),
          ToastLevel.Warning,
        ),
      );
    }
    console.error(error);
  }

  private mapResponseToState(testMode: boolean, getCatalogResponse: GetCatalogResponse, options?: {
    roomId?: string;
    departmentId?: string;
    programId?: string;
    projectId?: string;
    roomContentId?: string;
    categoryId?: string;
  }) {
    //Create state

    let companyId = getCatalogResponse.companyId;
    let roomModels = getCatalogResponse.rooms
      .filter((_) => (testMode || !_.name.startsWith("Auto_")) && _.isActive)
      .map((x) => RoomModel.fromDto(x));
    let departmentModels = getCatalogResponse.departments
      .filter((_) => (testMode || !_.name.startsWith("Auto_")) && _.isActive)
      .map((x) => DepartmentModel.fromDto(x));
    let projectModels = getCatalogResponse.projects
      .filter((_) => (testMode || !_.name.startsWith("Auto_")) && _.isActive)
      .map((x) => ProjectModel.fromDto(x));
    let programModels = getCatalogResponse.programs
      .filter((_) => (testMode || !_.name.startsWith("Auto_")) && _.isActive)
      .map((x) => ProgramModel.fromDto(x));
    let categoryModels = getCatalogResponse.categories
      .filter((_) => (testMode || !_.name.startsWith("Auto_")) && _.isActive)
      .map((x) => CategoryModel.fromDto(x));
    let roomContentModels = getCatalogResponse.roomContents
      .filter((_) => (testMode || !_.name.startsWith("Auto_")) && _.isActive)
      .sort((a, b) => {
          const sortKeyA = a.code || a.name;
          const sortKeyB = b.code || b.name;
          return sortKeyA.localeCompare(sortKeyB);
      })
      .map((x) => RoomContentModel.fromDto(x));

    let menuModel = MenuModel.fromDto(getCatalogResponse.menus);

    //Map relationships:

    getCatalogResponse.rooms.forEach((roomDto) => {
      let roomModel = roomModels.filter((x) => x.id === roomDto.id)[0];
      if (!roomModel) return;
    });

    getCatalogResponse.departments.forEach((departmentDto) => {
      let departmentModel = departmentModels.filter(
        (x) => x.id === departmentDto.id,
      )[0];
      if (!departmentModel) return;
    });

    getCatalogResponse.projects.forEach((projectDto) => {
      let projectModel = projectModels.filter((x) => x.id === projectDto.id)[0];
      if (!projectModel) return;
    });

    getCatalogResponse.programs.forEach((programDto) => {
      let programModel = programModels.filter((x) => x.id === programDto.id)[0];
      if (!programModel) return;
    });

    getCatalogResponse.categories.forEach((categoryDto) => {
      let categoryModel = categoryModels.filter(
        (x) => x.id === categoryDto.id,
      )[0];
      if (!categoryModel) return;
    });

    getCatalogResponse.roomContents.forEach((roomContentDto) => {
      let roomContentModel = roomContentModels.filter(
        (x) => x.id === roomContentDto.id,
      )[0];
      if (!roomContentModel) return;
    });

    //emit signals
    const {
      roomId,
      departmentId,
      programId,
      projectId,
      roomContentId,
      categoryId,
    } = options || {};


    if(roomId) {
      menuModel.rooms().forEach((model) => {
        model.isChecked.set(model.id === roomId);
      })
    }

    if(departmentId) {
      menuModel.departments().forEach((model) => {
        model.isChecked.set(model.id === departmentId);
      })
    }

    if(projectId) {
      menuModel.projects().forEach((model) => {
        model.isChecked.set(model.id === projectId);
      })
    }

    if(programId) {
      menuModel.programs().forEach((model) => {
        model.isChecked.set(model.id === programId);
      })
    }

    if(categoryId) {
      menuModel.categories().forEach((model) => {
        model.isChecked.set(model.id === categoryId);
      })
    }

    this.CatalogManagementState.rooms.set(roomModels);
    this.CatalogManagementState.departments.set(departmentModels);
    this.CatalogManagementState.projects.set(projectModels);
    this.CatalogManagementState.programs.set(programModels);
    this.CatalogManagementState.categories.set(categoryModels);
    this.CatalogManagementState.roomContents.set(roomContentModels);
    this.CatalogManagementState.menus.set(menuModel);

    this.CatalogManagementState.companyId = companyId;
  }




  public async getRoomProperty(
    roomId: string,
    departmentId?: string,
    programId?: string,
    projectId?: string
  ) {
    const cacheKey = this.createRoomPropertyCacheKey(roomId, departmentId, programId, projectId);
    const cached = this.RoomPropertiesState.get(cacheKey);
    if (cached) {
      return cached;
    }
  
    try {
      const response = await this.roomHttpService
        .getProperties(roomId, departmentId, programId, projectId)
        .response;
      const model = RoomPropertyModel.fromDto(response);
      this.updateMenuSelections(model.menus(), { roomId, departmentId, programId, projectId });
      this.CatalogManagementState.menus.set(model.menus());
      this.RoomPropertiesState.set(cacheKey, model);
      return model;
    } catch (error) {
      console.error("RoomProperty Failed to load:", error);
      return undefined;
    }
  }

  private createRoomPropertyCacheKey(
    roomId: string,
    departmentId?: string,
    programId?: string,
    projectId?: string
  ): string {
    return `${roomId}_${departmentId || 'null'}_${programId || 'null'}_${projectId || 'null'}`;
  }

  public async getDepartmentProperty(
    departmentId: string,
    programId?: string,
    projectId?: string,
  ) {
    const cached = this.getDepartmentPropertyFromCache(departmentId);
    if (cached) {
      return cached;
    }
  
    try {
      const response = await this.departmentHttpService
        .getProperties(departmentId, programId, projectId)
        .response;
  
      const model = DepartmentPropertyModel.fromDto(response);
      this.updateMenuSelections(model.menus(), { departmentId, programId, projectId});
      this.CatalogManagementState.menus.set(model.menus());
      this.DepartmentPropertiesState.update((current) => [...current, model]);
      return model;
    } catch (error) {
      console.error("DepartmentProperty Failed to load:", error);
      return undefined;
    }
  }

  private getDepartmentPropertyFromCache(departmentId: string): DepartmentPropertyModel | undefined {
    return this.DepartmentPropertiesState().find(p => p.id === departmentId);
  }


  public async getProgramProperty(
    programId: string
  ) {
    const cached = this.getProgramPropertyFromCache(programId);
    if (cached) {
      return cached;
    }
  
    try {
      const response = await this.programHttpService
        .getProperties(programId)
        .response;
  
      const model = ProgramPropertyModel.fromDto(response);
      this.updateMenuSelections(model.menus(), { programId });
      this.CatalogManagementState.menus.set(model.menus());
      this.ProgramPropertiesState.update((current) => [...current, model]);
      return model;
    } catch (error) {
      console.error("ProgramProperty Failed to load:", error);
      return undefined;
    }
  }

  private getProgramPropertyFromCache(programId: string): ProgramPropertyModel | undefined {
    return this.ProgramPropertiesState().find(p => p.id === programId);
  }

  public async getProjectProperty(
    projectId: string
  ) {
    const cached = this.getProjectPropertyFromCache(projectId);
    if (cached) {
      return cached;
    }
  
    try {
      const response = await this.projectHttpService
        .getProperties(projectId)
        .response;
  
      const model = ProjectPropertyModel.fromDto(response);
      this.updateMenuSelections(model.menus(), { projectId });
      this.CatalogManagementState.menus.set(model.menus());
      this.ProjectPropertiesState.update((current) => [...current, model]);
      return model;
    } catch (error) {
      console.error("ProjectProperty Failed to load:", error);
      return undefined;
    }
  }

  private getProjectPropertyFromCache(projectId: string): ProjectPropertyModel | undefined {
    return this.ProjectPropertiesState().find(p => p.id === projectId);
  }

  public async getRoomContentProperty(
    roomContentId: string,
    programId?: string,
    projectId?: string
  ) {
    const cached = this.getRoomContentPropertyFromCache(roomContentId);
    if (cached) {
      return cached;
    }
  
    try {
      const response = await this.roomContentHttpService
        .getProperties(roomContentId, programId, projectId)
        .response;
  
      const model = RoomContentPropertyModel.fromDto(response);
      this.RoomContentPropertiesState.update((current) => [...current, model]);
      return model;
    } catch (error) {
      console.error("RoomContentProperty Failed to load:", error);
      return undefined;
    }
  }

  private getRoomContentPropertyFromCache(roomContentId: string): RoomContentPropertyModel | undefined {
    return this.RoomContentPropertiesState().find(p => p.id === roomContentId);
  }

  private updateMenuSelections(menuModel: MenuModel, options: FilterOptions) {
    if(options.roomId) {
      menuModel?.rooms().forEach((model) => {
        model.isChecked.set(model.id === options.roomId);
      })
    }

    if(options.departmentId) {
      menuModel?.departments().forEach((model) => {
        model.isChecked.set(model.id === options.departmentId);
      })
    }

    if(options.projectId) {
      menuModel?.projects().forEach((model) => {
        model.isChecked.set(model.id === options.projectId);
      })
    }

    if(options.programId) {
      menuModel?.programs().forEach((model) => {
        model.isChecked.set(model.id === options.programId);
      })
    }

    if(options.categoryId) {
      menuModel?.categories().forEach((model) => {
        model.isChecked.set(model.id === options.categoryId);
      })
    }
  }


  public syncRouteStateWithBreadCrumbs(route: ActivatedRoute) {
    this._catalogRouteStateHandler.syncRouteWithBreadcrumbs(route);
  }

  public async printRoomProperties(roomId: string): Promise<void> {
    try {
          const result = this.roomHttpService.printRoomProperties(roomId);
          const data = await result.response;
          const link = document.createElement('a');
          link.href = data.url;
          link.download = roomId + '_properties';
          link.target = '_blank';
          link.click();
        } catch (error) {
          this.messenger.send(ToastMessage.create(new UnhandledErrorMessage(),ToastLevel.Warning));
        } finally {
          this.messenger.send(new PrintRoomPropertiesLoadedMessage());
        }
  }

  ngOnDestroy(): void {
    this.messenger.unsubscribeByOwner(this);
    this._requestQueue$.complete();
    this._initialized = false;
  }



}